import { OPTION_TABLES } from '../api/optionTable.js'
import { DEFAULT_API } from './api.js'

// Hints name only the chosen API's form (src/api.js), whichever package the
// model code called.
const TABLES = { modeling: OPTION_TABLES['@jscad/modeling'], fluent: OPTION_TABLES['@jbroll/jscad-fluent'] }
const tableFor = (api) => TABLES[api] ?? TABLES[DEFAULT_API]

export const TAPER = {
  modeling: 'a taper (cone) is primitives.cylinderElliptic({ startRadius: [r, r], endRadius: [r, r], height }); start is the -Z end',
  fluent: 'a taper (cone) is jf.cylinder({ radius: [start, end], height }); start is the -Z end',
}
const taperFor = (api) => TAPER[api] ?? TAPER[DEFAULT_API]

const words = (name) => name.split(/(?<=[a-z])(?=[A-Z0-9])|(?<=\d)(?=[A-Za-z])|_+/).map((w) => w.toLowerCase())
const wordKey = (name) => [...words(name)].sort().join(' ')
const baseName = (fn) => fn.slice(fn.lastIndexOf('.') + 1)

const TAPER_WORDS = new Set(['taper', 'cone', 'frustum'])
const RADIUS_WORDS = new Set(['r', 'radius', 'd', 'diameter'])
const END_WORDS = new Set(['start', 'end', 'top', 'bottom', 'base', 'tip', 'upper', 'lower', '1', '2'])

const isTaperOption = (fn, option) => {
  if (baseName(fn) !== 'cylinder') return false
  const ws = words(option)
  return ws.some((w) => TAPER_WORDS.has(w)) || (ws.some((w) => RADIUS_WORDS.has(w)) && ws.some((w) => END_WORDS.has(w)))
}

// Siblings the name alone does not pair (square→rectangle, circle→ellipse,
// sphere→ellipsoid; cube→cuboid and cylinder→cylinderElliptic pair by name).
const ALIASES = new Map([
  ['square', 'rectangle'],
  ['circle', 'ellipse'],
  ['sphere', 'ellipsoid'],
])

// Siblings share a name (cylinder, cylinderElliptic) or the stem of their last
// word (cube, cuboid, roundedCuboid).
const related = (a, b) => {
  const x = a.toLowerCase()
  const y = b.toLowerCase()
  return ALIASES.get(x) === y || ALIASES.get(y) === x || x.includes(y) || y.includes(x) || words(a).at(-1).slice(0, 3) === words(b).at(-1).slice(0, 3)
}

const siblings = (fn, api) => {
  const table = tableFor(api)
  const base = baseName(fn)
  return Object.keys(table.options)
    .map((path) => ({ path, name: table.prefix + path }))
    .filter(({ path, name }) => name !== fn && related(base, baseName(path)))
}

const explainUnknown = ({ fn, option, suggestions }, api) => {
  if (isTaperOption(fn, option)) return { fn, option, suggestions: [], hint: taperFor(api) }
  const table = tableFor(api)
  const key = wordKey(option)
  const matches = []
  for (const { path, name } of siblings(fn, api)) {
    const known = table.options[path]
    const match = known.find((o) => o === option) ?? known.find((o) => wordKey(o) === key)
    if (match) matches.push({ name, option: match })
  }
  if (!matches.length) return { fn, option, suggestions }
  // radiusStart's in-function suggestion (radius) is wrong advice once a
  // sibling takes the same words in another order (startRadius).
  const reordered = matches.some((m) => m.option !== option)
  const hint = matches.slice(0, 3).map((m) => `${m.name} takes ${m.option}`).join('; ')
  return { fn, option, suggestions: reordered || !suggestions.length ? [] : suggestions, hint }
}

const article = (type) => (type === 'array' ? 'an array' : 'a number')

// The chosen API's function with the called one's name, when it takes the
// option with the same type (jf.cylinder's radius takes an array too).
const counterpart = (fn, option, expected, api) => {
  const table = tableFor(api)
  const [cls, method] = fn.split('.')
  if (table.methodTypes?.[cls]?.[method]?.[option] === expected) return fn
  const base = baseName(fn)
  const path = Object.keys(table.types ?? {}).find((p) => baseName(p) === base && table.types[p][option] === expected)
  return path === undefined ? null : table.prefix + path
}

const typeHint = ({ fn, option, expected, got }, api) => {
  const own = counterpart(fn, option, expected, api)
  const parts = own ? [`${own} takes ${option} as ${article(expected)}`] : []
  if (baseName(fn) === 'cylinder' && option === 'radius' && got === 'array') return [...parts, taperFor(api)].join('; ')
  const table = tableFor(api)
  const sibling = siblings(fn, api).find(({ path }) => table.types?.[path]?.[option] === got)
  if (sibling) parts.push(`for ${article(got)} ${option} use ${sibling.name}`)
  return parts.length ? parts.join('; ') : `${option} takes ${article(expected)}`
}

const angleHint = ({ value }) => `${value} looks like degrees; angles are radians, so use ${value} * Math.PI / 180`

const round = (x) => Number(x.toFixed(6))

const reversedCall = (fn, api) => {
  if (api === 'fluent') return 'jf.polygon([...points].reverse())'
  if (fn === 'geometries.geom2.fromPoints') return 'geometries.geom2.fromPoints([...points].reverse())'
  return 'primitives.polygon({ points: [...points].reverse() })'
}

const windingHint = ({ fn, area, reversed }, api) =>
  reversed
    ? `points ran clockwise (area ${round(area)}); reversed them so extrusions come out right side out`
    : `points run clockwise (area ${round(area)}), so an extrusion of this outline comes out inside out: list them counter-clockwise, e.g. ${reversedCall(fn, api)}`

const BOUNDING_BOX = { fluent: 'shape.measureBoundingBox()', modeling: 'measurements.measureBoundingBox(shape)' }
const EMPTY_CAUSE = {
  subtract: 'what it removed covers all of the first shape',
  intersect: 'the shapes do not overlap',
}
const emptyHint = ({ empty }, api) =>
  `${empty} returned an empty shape: ${EMPTY_CAUSE[empty]}; compare their bounding boxes with ${BOUNDING_BOX[api] ?? BOUNDING_BOX[DEFAULT_API]}`

// Fluent's own constructors and booleans already throw a TypeError naming
// jf.polyhedron; only modeling's throw ("only unions of the same type are
// supported") leaves the fix unstated.
const meshHint = (api) =>
  api === 'modeling' ? 'an operand is { points, faces } data, not a shape: make it one with primitives.polyhedron({ points, faces })' : undefined

/**
 * The warning a model sees for a fact the option checks found: an unknown
 * option `{ fn, option, suggestions }`, a mistyped one `{ fn, option, expected, got }`,
 * an angle over 2π `{ fn, option, value }`, clockwise outline points
 * `{ fn, option, area, reversed? }`, an empty boolean result `{ fn, empty }` or
 * `{ points, faces }` data given to a boolean `{ fn, meshOperand }`, a
 * roundRadius reduced to fit `{ fn, option, clamped, from, to, least | height | radius }`
 * a number radius made a pair `{ fn, option, coerced, from, to }` or a
 * jf.polygon options object unwrapped to its points `{ fn, option, unwrapped }`.
 */
export const explainWarning = (fact, api = DEFAULT_API) => {
  const { fn, option } = fact
  if (fact.clamped) return { fn, option, hint: clampHint(fact) }
  if (fact.coerced) return { fn, option, hint: pairHint(fact) }
  if (fact.unwrapped) return { fn, option, hint: UNWRAP_HINT }
  if (fact.flat) return { fn, option, hint: flatHint(fn, api) }
  if (Array.isArray(fact.suggestions)) return explainUnknown(fact, api)
  if (fact.expected) return { fn, option, hint: typeHint(fact, api) }
  if ('value' in fact) return { fn, option, hint: angleHint(fact) }
  if ('area' in fact) return { fn, option, hint: windingHint(fact, api) }
  if (fact.empty) return { fn, hint: emptyHint(fact, api) }
  if (fact.meshOperand) return { fn, hint: meshHint(api) }
  if (fact.ignored) return { fn, option, hint: fact.hint }
  return fact
}

const halfOf = (what, value) => `under half the ${what}, ${value} / 2 = ${round(value / 2)}`
const tooBig = (roundRadius, rule) => `roundRadius ${roundRadius} is too big: it must be ${rule}`

// A roundRadius the checks reduced to fit: `least` (the smallest size),
// `height` or `radius` names the limit it met.
const clampHint = ({ from, to, least, height, radius }) => {
  const rule = least !== undefined ? halfOf('smallest size', least) : height !== undefined ? halfOf('height', height) : `at most the radius, ${radius}`
  return `${tooBig(from, rule)}; used ${to}`
}

const pairHint = ({ option, from, to }) => `${option} takes an [x, y] pair of radii; used [${to.join(', ')}] for ${from}`

const UNWRAP_HINT = "jf.polygon takes the points array itself, jf.polygon([[x, y], ...]); used the object's points"

const flatHint = (fn, api) => {
  const name = api === 'modeling' ? fn : fn.split('.').pop()
  const extruded = api === 'modeling' ? `${name}(angle, extrusions.extrudeLinear({ height }, shape))` : `shape.extrudeLinear({ height }).${name}(...)`
  return `${name} only squashes a 2D shape, which stays flat in the XY plane; extrude it first and rotate the 3D shape: ${extruded}`
}

// Most NaN options come from arithmetic on a value that was undefined: a
// property the object lacks, a misspelled name, a parameter read as an object.
const NAN_CAUSE = 'an undefined or NaN value in the arithmetic that made it gives NaN: a property the object does not have, a misspelled name, or a parameter read back as an object (docs params)'
const PARAM_KEYS = ['default', 'initial', 'type', 'min', 'max', 'step']
const PARAM_DEF = 'a parameter definition, not its value: read the value from the values main is given; docs params shows how'

const isNaNNumber = (value) => typeof value === 'number' && Number.isNaN(value)
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

const objectWhat = (name, value) => {
  const keys = Object.keys(value)
  const shown = keys.length ? `an object with { ${keys.slice(0, 6).join(', ')} }` : 'an empty object'
  return keys.some((key) => PARAM_KEYS.includes(key)) ? `${name} is ${shown}: ${PARAM_DEF}` : `${name} is ${shown}, not a number`
}

const nanOf = (option, value, type) => {
  if (type === 'number' && isNaNNumber(value)) return `${option} is NaN: ${NAN_CAUSE}`
  if (type === 'number' && isObject(value)) return objectWhat(option, value)
  if (type === 'array' && Array.isArray(value)) {
    const nan = value.flatMap((v, i) => (isNaNNumber(v) ? [`${option}[${i}]`] : []))
    if (nan.length) return `${nan.join(', ')} ${nan.length > 1 ? 'are' : 'is'} NaN: ${NAN_CAUSE}`
    const at = value.findIndex(isObject)
    if (at >= 0) return objectWhat(`${option}[${at}]`, value[at])
  }
  return undefined
}

/**
 * Why a call's number or array option is NaN or an object, for the error the
 * call threw. `types` maps each option to 'number' or 'array'.
 * @param {unknown} options
 * @param {Record<string, string> | undefined} types
 */
export const explainNaN = (options, types) => {
  if (!isObject(options) || !types) return undefined
  for (const [option, type] of Object.entries(types)) {
    const what = nanOf(option, options[option], type)
    if (what) return what
  }
  return undefined
}

const lookups = new WeakMap()

const lookupFor = (index) => {
  if (lookups.has(index)) return lookups.get(index)
  const classes = new Set(index.filter((e) => e.kind === 'class' && e.pkg === '@jbroll/jscad-fluent').map((e) => e.name))
  /** @type {Map<string, string[]>} */
  const methods = new Map()
  /** @type {Map<string, object[]>} */
  const functions = new Map()
  const namespaces = new Set(index.filter((e) => e.kind === 'namespace' && e.pkg === '@jscad/modeling' && !e.name.includes('.')).map((e) => e.name))
  const jfNames = new Set(index.filter((e) => e.pkg === '@jbroll/jscad-fluent' && /^jf\.[\w$]+$/.test(e.name)).map((e) => e.name.slice(3)))
  for (const e of index) {
    if (e.kind !== 'function') continue
    const dot = e.name.lastIndexOf('.')
    if (dot < 0) continue
    const name = e.name.slice(dot + 1)
    const owner = e.name.slice(0, dot)
    if (classes.has(owner)) methods.set(name, [...(methods.get(name) ?? []), owner])
    // Nested helpers (maths.vec3.scale, extrusions.slice.transform) share the operations' names.
    else if (e.pkg === '@jscad/modeling' && !owner.includes('.')) functions.set(name, [...(functions.get(name) ?? []), e])
  }
  const lookup = { methods, functions, namespaces, jfNames }
  lookups.set(index, lookup)
  return lookup
}

const callOf = (entry) => {
  const params = /\(([^)]*)\)/.exec(entry.signature)?.[1] ?? ''
  return `${entry.name}(${params.replace(/\.\.\.\w+/g, 'shape')})`
}

const NOT_A_FUNCTION = /(\S+) is not a function/
const NOT_DEFINED = /\b([A-Za-z_$][\w$]*) is not defined\b/
// The frame's bundles are minified, so the class name is often one letter.
const CLASS_WITHOUT_NEW = /Class constructor [\w$]+ cannot be invoked without 'new'/
const POINTS_AS_GEOM2 = /FluentGeom2 expected a geom2 \(fluent or raw\), got an array/

// A bare modeling name the model code never took from its namespace.
const explainUndefined = (name, api, { methods, functions, namespaces, jfNames }) => {
  if (api === 'fluent') {
    const classes = methods.get(name)
    if (jfNames.has(name)) return classes ? `use jf.${name}(...), or shape.${name}(...) on a jf shape` : `use jf.${name}(...)`
    return classes ? `${name} is a method of ${classes.join(', ')}: use shape.${name}(...)` : undefined
  }
  if (namespaces.has(name)) return `${name} is a namespace: const { ${name} } = require('@jscad/modeling')`
  const entries = functions.get(name)
  if (!entries) return undefined
  const owners = [...new Set(entries.map((e) => e.name.slice(0, e.name.lastIndexOf('.'))))]
  return owners.map((owner) => `${name} is in ${owner}: const { ${name} } = require('@jscad/modeling').${owner}`).join('; or ')
}

/**
 * A hint for "X is not a function" when X names a fluent method or a modeling
 * function, or for "X is not defined" when X is a modeling function or
 * namespace name, in the chosen API's form.
 * @param {string} message
 * @param {{ api?: string, index: Array<object> }} options index is api/index.json
 */
export const explainError = (message, { api = DEFAULT_API, index }) => {
  if (!index) return undefined
  if (/'return' outside of function/.test(message ?? '')) {
    return 'a file or execute snippet is a module, not a function body: console.log the value, or export it, module.exports = { main: () => shape }'
  }
  if (api === 'fluent' && POINTS_AS_GEOM2.test(message ?? '')) {
    return 'FluentGeom2 takes a geom2, or a list of three or more [x, y] points as jf.polygon(points) does; for several shapes, jf.geom2Array(...shapes)'
  }
  if (CLASS_WITHOUT_NEW.test(message ?? '')) {
    return api === 'fluent'
      ? 'a class was called without new: the jf wrappers are classes, so write new jf.FluentGeom3(shape) or new jf.FluentGeom2(shape), never jf.FluentGeom3(shape)'
      : 'a class was called without new; call it as new Name(...)'
  }
  const undefinedName = NOT_DEFINED.exec(message ?? '')
  if (undefinedName?.[1] === 'EOF') return 'EOF is a line left from a shell heredoc (cat <<EOF); there is no shell here, so write the file content without it'
  if (undefinedName) return explainUndefined(undefinedName[1], api, lookupFor(index))
  const match = NOT_A_FUNCTION.exec(message ?? '')
  if (!match) return undefined
  const expr = match[1]
  const dot = expr.lastIndexOf('.')
  const name = expr.slice(dot + 1)
  const receiver = dot < 0 ? null : expr.slice(0, dot)
  if (!/^[A-Za-z_$][\w$]*$/.test(name)) return undefined
  if (TAPER_WORDS.has(name.toLowerCase())) return taperFor(api)
  const { methods, functions } = lookupFor(index)
  if (api === 'fluent') {
    const classes = methods.get(name)
    if (!classes) return undefined
    const head = `${name} is a method of ${classes.join(', ')}`
    if (receiver && receiver !== 'jf') {
      return (
        `${head}; the value it is called on is not a jf shape: a plain @jscad/modeling shape, such as an .scad library or jscad-text gives, or a plain array has no methods. ` +
        `Make it with jf.*, or wrap it: new jf.FluentGeom3(shape) (new jf.FluentGeom2 for 2D), jf.geom3Array(...shapes) for a list; then use shape.${name}(...)`
      )
    }
    return `${head}: use shape.${name}(...)`
  }
  const entries = functions.get(name)
  if (!entries) return undefined
  const use = `use ${entries.map(callOf).join(' or ')}`
  return receiver && receiver !== 'jf' ? `@jscad/modeling shapes have no methods; ${use}` : use
}

/** The message with explainError's hint on a line of its own, added once. */
export const withErrorHint = (message, options) => {
  const hint = explainError(message, options)
  return hint && !message.includes(hint) ? `${message}\n${hint}` : message
}
